import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Zap, MessageCircle, IndianRupee } from "lucide-react";
import { scrollTo, WHATSAPP_LINK } from "@/lib/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const Chip = ({ icon: Icon, text, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.5 }}
    className={`glass absolute z-20 flex items-center gap-2 rounded-full px-4 py-2 font-display text-[10px] font-bold uppercase tracking-[0.15em] text-white shadow-[0_10px_30px_rgba(0,0,0,0.5)] ${className}`}
  >
    <Icon className="h-3.5 w-3.5 text-accent" />
    {text}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMocks = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="noise relative flex min-h-screen flex-col overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-[-20%] h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-royal/15 blur-[140px]" />
        <div className="absolute bottom-[-10%] right-[-10%] h-[50vh] w-[50vw] rounded-full bg-accent/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      </div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-6 pb-10 pt-16 text-center md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="hero-badge"
          className="glass flex items-center gap-2 rounded-full px-5 py-2"
        >
          <Zap className="h-3.5 w-3.5 text-accent" />
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-200 sm:text-xs">
            24-Hour Website Launch
          </span>
        </motion.div>

        <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
          <Line delay={0.15}>We build websites</Line>
          <Line delay={0.28}>that make your</Line>
          <Line delay={0.41}>
            business <span className="text-gradient">look bigger.</span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7 }}
          className="mt-7 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-display text-sm font-extrabold uppercase tracking-[0.15em] sm:text-base"
        >
          <span className="text-accent" data-testid="hero-usp-24h">Live in 24 Hours.</span>
          <span className="hidden h-1 w-1 rounded-full bg-zinc-600 sm:block" />
          <span className="text-zinc-300" data-testid="hero-usp-price">Premium Quality. Half the Market Price.</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.78, duration: 0.7 }}
          className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 md:text-base"
        >
          Your website is often the first impression customers have of your business. We create modern,
          professional websites that help your business look credible, communicate your services and
          generate enquiries.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-9 flex flex-col items-center gap-4 sm:flex-row"
        >
          <button
            onClick={() => scrollTo("#enquiry")}
            data-testid="hero-get-website-btn"
            className="group flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,box-shadow,transform] duration-300 hover:bg-white hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]"
          >
            Get My Website Live
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollTo("#pricing")}
            data-testid="hero-view-pricing-btn"
            className="rounded-full border border-white/20 px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-white transition-[border-color,background-color] duration-300 hover:border-accent/60 hover:bg-white/5"
          >
            View Pricing
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-5 text-[11px] tracking-wide text-zinc-600"
        >
          Fast launch available for eligible standard website projects.
        </motion.p>
      </motion.div>

      <motion.div style={{ y: yMocks }} className="relative z-0 mx-auto w-full max-w-6xl px-6 pb-16" data-testid="hero-mockups">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-5xl"
        >
          <div className="glow-accent overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.65)]">
            <img
              src="/assets/falkeen-home-hero.webp"
              alt="Laptop and smartphone displaying professional business websites built by Falkeen Technologies"
              className="aspect-video w-full object-cover"
              data-testid="hero-image"
            />
          </div>
          <Chip icon={Zap} text="Live in 24 Hours" className="max-sm:hidden -left-3 top-6 lg:-left-8" delay={1.5} />
          <Chip icon={IndianRupee} text="From ₹14,999" className="max-sm:hidden -right-3 top-10 lg:-right-8" delay={1.65} />
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Chip icon={MessageCircle} text="WhatsApp Ready" className="max-sm:hidden -bottom-5 left-1/2 z-30 -translate-x-1/2 cursor-pointer" delay={1.8} />
          </a>
        </motion.div>
      </motion.div>
      <div className="h-4 md:hidden" />
    </section>
  );
};
